import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { db } from '../../firebase';
import { logIn, useUserAuth } from '../../UserAuth';
import { archiveUserDeck, loadUserDecks } from '../../utils/deck_service';
import { useViewportHeight } from '../../utils/viewportUtils';
import { Icon } from '../Icon';
import { MaterialHeader } from '../MaterialHeader';

export function ArchivedDecksPage() {
  const { user } = useUserAuth();
  const history = useHistory();
  const viewportHeight = useViewportHeight();
  const [decks, setDecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoringId, setRestoringId] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      setDecks(await loadUserDecks(db, user.uid, { archived: true }));
    } catch (loadError) {
      setError(loadError.message || 'Unable to load archived decks.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  const restore = async deckId => {
    if (restoringId) {
      return;
    }
    setRestoringId(deckId);
    setError('');
    try {
      await archiveUserDeck(db, user.uid, deckId, false);
      history.push(`/deck?id=${deckId}`);
    } catch (restoreError) {
      setError(restoreError.message || 'Unable to restore this deck.');
      setRestoringId('');
    }
  };

  return (
    <div style={{
      height: viewportHeight,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      <MaterialHeader
        variant="close"
        title="Archived Decks"
        onClose={() => history.push('/')}
      />
      <main style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '16px' }}>
        <div style={{ maxWidth: '760px', margin: '0 auto' }}>
          {!user ? (
            <md-elevated-card style={{ display: 'block', padding: '32px', textAlign: 'center' }}>
              <h2>Sign in to view archived decks</h2>
              <md-filled-button onClick={logIn}>Sign In</md-filled-button>
            </md-elevated-card>
          ) : loading ? (
            <div role="status" style={{ display: 'grid', placeItems: 'center', padding: '48px' }}>
              <md-circular-progress indeterminate></md-circular-progress>
            </div>
          ) : (
            <>
              {error && (
                <div role="alert" style={{ marginBottom: '12px' }}>
                  {error} <button type="button" onClick={load}>Retry</button>
                </div>
              )}
              {decks.map(deck => (
                <md-elevated-card
                  key={deck.id}
                  style={{ display: 'block', padding: '20px', marginBottom: '12px' }}
                >
                  <h2 style={{ margin: '0 0 8px' }}>
                    {typeof deck.deck_name === 'string' && deck.deck_name.trim()
                      ? deck.deck_name.trim()
                      : 'Untitled Deck'}
                  </h2>
                  <p style={{ color: 'var(--md-sys-color-on-surface-variant)' }}>
                    {(Number.isInteger(deck.verse_count) ? deck.verse_count : 0)
                      .toLocaleString()} verses · {deck.learned_count || 0} learned
                  </p>
                  <md-filled-button
                    {...(restoringId ? { disabled: true } : {})}
                    onClick={() => restore(deck.id)}
                  >
                    <Icon name="archive" slot="icon" />
                    {restoringId === deck.id ? 'Restoring…' : 'Restore Deck'}
                  </md-filled-button>
                </md-elevated-card>
              ))}
              {decks.length === 0 && !error && (
                <md-elevated-card style={{ display: 'block', padding: '32px', textAlign: 'center' }}>
                  <Icon name="archive" size="hero" ariaHidden />
                  <h2>No archived decks</h2>
                  <p style={{ color: 'var(--md-sys-color-on-surface-variant)' }}>
                    Decks you archive appear here so you can restore them later.
                  </p>
                </md-elevated-card>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
